import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { IonIcon } from '@ionic/angular';
import { addIcons } from 'ionicons';
import { close } from 'ionicons/icons';

import { PushNotificationService } from '@core/services';
import { iconFor, registerNotificationIcons } from '@shared/utils/notification-icon.util';

/** Long enough to read two lines, short enough not to sit over the header. */
const AUTO_HIDE_MS = 5000;

/**
 * Shows a push that arrives while the app is on screen, in place of the
 * system notification the service worker would otherwise raise. Styled like a
 * row on the Notifikasi page. Tapping anywhere dismisses it; it does not
 * navigate, since notifications carry no link yet.
 *
 * Mounted once in app.html. `position: fixed` within ion-app, above the
 * router outlet, so it shows over whatever page is open.
 */
@Component({
  selector: 'co-push-alert',
  imports: [IonIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './push-alert.html',
  styleUrl: './push-alert.scss',
})
export class CoPushAlert {
  private readonly push = inject(PushNotificationService);

  protected readonly message = this.push.foreground;
  protected readonly icon = computed(() => iconFor(this.message()?.kind));

  constructor() {
    registerNotificationIcons();
    addIcons({ close });

    /* Keyed on the push itself, so a newer one restarts the timer. */
    effect((onCleanup) => {
      if (!this.message()) {
        return;
      }
      const timer = setTimeout(() => this.push.dismissForeground(), AUTO_HIDE_MS);
      onCleanup(() => clearTimeout(timer));
    });
  }

  protected dismiss(): void {
    this.push.dismissForeground();
  }
}
